import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Divider,
  InputAdornment,
  Skeleton,
  Slider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  fetchNotificationSettings,
  updateNotificationSettings,
} from "../../../../store/remediation/remediationSlice";
import { friendlyErrorMessage } from "../common/ErrorCodeMap";
import { useRemediationSnackbar } from "../common/SnackbarHost";
import { formatAbsolute, useActionFeedback } from "../common/hooks";

/**
 * Admin-only Notification Settings tab.
 *
 * Writable knob: dormancy_nudge_days (1–90).
 * The kill-switch fields are read-only and only surface as a banner when on.
 */
const DORM_MIN = 1;
const DORM_MAX = 90;

const NotificationSettingsPanel = () => {
  const dispatch = useDispatch();
  const { showSnackbar } = useRemediationSnackbar();
  const slice = useSelector((s) => s.remediation.notificationSettings);
  const updateSlice = useSelector((s) => s.remediation.notificationSettingsUpdate);
  const [dormDays, setDormDays] = useState(7);
  const saving = updateSlice?.status === "loading";

  useEffect(() => {
    if (!slice.data) {
      dispatch(fetchNotificationSettings());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (slice.data) {
      setDormDays(slice.data.dormancy_nudge_days ?? 7);
    }
  }, [slice.data]);

  const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, Math.round(Number(n) || 0)));

  const handleSave = () => {
    dispatch(
      updateNotificationSettings({
        dormancy_nudge_days: clamp(dormDays, DORM_MIN, DORM_MAX),
      })
    );
  };

  useActionFeedback("updateNotificationSettings", ({ error }) => {
    if (error) {
      showSnackbar({ message: friendlyErrorMessage(error), severity: "error" });
    } else {
      showSnackbar({ message: "Notification settings saved.", severity: "success" });
    }
  });

  const handleReset = () => {
    if (slice.data) {
      setDormDays(slice.data.dormancy_nudge_days ?? 7);
    }
  };

  const dirty = useMemo(() => {
    if (!slice.data) return false;
    return Number(dormDays) !== slice.data.dormancy_nudge_days;
  }, [dormDays, slice.data]);

  if (slice.status === "loading" && !slice.data) {
    return (
      <Stack spacing={2}>
        <Skeleton variant="rectangular" height={80} />
        <Skeleton variant="rectangular" height={140} />
      </Stack>
    );
  }

  return (
    <Box sx={{ maxWidth: 720 }}>
      {slice.data?.is_paused && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Remediation is paused</AlertTitle>
          <Box sx={{ fontSize: 13 }}>
            Paused at <strong>{formatAbsolute(slice.data.paused_at)}</strong>
            {slice.data.paused_by ? <> by <strong>{slice.data.paused_by}</strong></> : null}
            {slice.data.paused_reason ? <> — {slice.data.paused_reason}</> : null}.
          </Box>
        </Alert>
      )}

      {slice.status === "failed" && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {friendlyErrorMessage(slice.error)}
        </Alert>
      )}

      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
        Dormancy nudge cadence
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
        How many days a task can sit in <strong>Awaiting Approval</strong>, <strong>Permission Denied</strong>,
        or <strong>Failed</strong> before we email you again. Raise to reduce noise.
      </Typography>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Slider
          value={Number(dormDays) || 0}
          min={DORM_MIN}
          max={DORM_MAX}
          step={1}
          onChange={(_e, v) => setDormDays(v)}
          sx={{ flexGrow: 1 }}
        />
        <TextField
          size="small"
          type="number"
          value={dormDays}
          onChange={(e) => setDormDays(clamp(e.target.value, DORM_MIN, DORM_MAX))}
          InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
          sx={{ width: 140 }}
        />
      </Stack>

      <Divider sx={{ my: 3 }} />

      <Stack direction="row" spacing={1.5}>
        <Button onClick={handleReset} disabled={!dirty || saving}>
          Reset
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!dirty || saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </Stack>
    </Box>
  );
};

export default NotificationSettingsPanel;
